// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg43 — optional ownerFilter prop: shows only fields the given owner completes ('client' | 'attorney'); auto fields are never shown; sections left empty by the filter collapse out of the steps — delivered 2026-07-04
// pkg42 — optional submitLabel prop (attorney answer-editing reuses the wizard with "Save Answers"); default unchanged — delivered 2026-07-04
// pkg36 — ModuleWizard: shared step-by-step wizard for assembly modules — delivered 2026-07-03
//
// One schema section per screen (parenting-plan pattern), with:
//   - a segment progress bar + "Step N of M" line,
//   - show_if conditional fields (hidden until the controlling answer matches;
//     hidden fields are never validated and render with the "because you
//     answered…" indent from the approved mockup),
//   - per-step required-field validation before Next,
//   - optional best-effort auto-save on every step change (onStepSave) — a
//     failed save never blocks navigation,
//   - Submit on the final step.
//
// Used by pages/AiModulePage.js (client portal) and
// pages/AdminInternalModuleFillPage.js (staff, admin portal).

import React, { useState } from 'react';
import { moduleCompletedBy, fieldOwner } from '../utils/fieldOwnership'; // pkg43

function FieldInput({ field, value, onChange }) {
  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '8px 11px', fontSize: 14,
    fontFamily: 'Arial, sans-serif',
    border: '1px solid #ccc', borderRadius: 4,
  };

  switch (field.type) {
    case 'multiline':
      return (
        <textarea
          rows={4}
          value={value ?? ''}
          placeholder={field.placeholder || ''}
          onChange={e => onChange(e.target.value)}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      );
    case 'date':
      return (
        <input
          type="date"
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          style={{ ...inputStyle, maxWidth: 220 }}
        />
      );
    case 'yes_no':
      return (
        <div style={{ display: 'flex', gap: 20, marginTop: 2 }}>
          {['yes', 'no'].map(opt => (
            <label key={opt} style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="radio"
                name={field.id}
                value={opt}
                checked={value === opt}
                onChange={() => onChange(opt)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              {opt === 'yes' ? 'Yes' : 'No'}
            </label>
          ))}
        </div>
      );
    case 'dropdown':
      return (
        <select
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          style={{ ...inputStyle, maxWidth: 320 }}
        >
          <option value="">— Select —</option>
          {(field.options || []).map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    default: // 'text'
      return (
        <input
          type="text"
          value={value ?? ''}
          placeholder={field.placeholder || ''}
          onChange={e => onChange(e.target.value)}
          style={{ ...inputStyle, maxWidth: 480 }}
        />
      );
  }
}

// A field is visible when it has no condition, or the controlling answer
// matches. Values compare as strings ('yes'/'no' for yes_no, the option
// string for dropdowns).
function isVisible(field, answers) {
  if (!field.show_if) return true;
  const current = answers[field.show_if.field];
  return current !== undefined && current !== null &&
         String(current) === String(field.show_if.equals);
}

export default function ModuleWizard({ schema, answers, onChange, onStepSave, onSubmit, submitting, submitLabel, ownerFilter }) { // pkg42 submitLabel / pkg43 ownerFilter — both optional
  // pkg43 — with an ownerFilter, only that owner's fields render (auto fields
  // never render for anyone); sections with nothing left collapse entirely.
  // show_if conditions still evaluate against the FULL answers object, so a
  // client field gated on an attorney answer behaves correctly.
  const cb = moduleCompletedBy(schema);
  const allSections = schema?.sections || [];
  const sections = !ownerFilter
    ? allSections.map(sec => ({ ...sec, fields: (sec.fields || []).filter(f => fieldOwner(f, cb) !== 'auto') }))
        .filter(sec => sec.fields.length)
    : allSections.map(sec => ({ ...sec, fields: (sec.fields || []).filter(f => fieldOwner(f, cb) === ownerFilter) }))
        .filter(sec => sec.fields.length);
  const [stepIdx, setStepIdx] = useState(0);
  const [errs,    setErrs]    = useState({});
  const [saveState, setSaveState] = useState(''); // '', 'saving', 'saved'

  const section = sections[stepIdx] || { fields: [] };
  const isLast  = stepIdx === sections.length - 1;

  function visibleFields(sec) {
    return (sec.fields || []).filter(f => isVisible(f, answers));
  }

  function validateStep() {
    const bad = {};
    for (const f of visibleFields(section)) {
      if (!f.required) continue;
      const v = answers[f.id];
      if (v === undefined || v === null || v === '') bad[f.id] = 'This field is required.';
    }
    return bad;
  }

  async function saveProgress() {
    if (!onStepSave) return;
    setSaveState('saving');
    try {
      await onStepSave();
      setSaveState('saved');
      setTimeout(() => setSaveState(''), 2500);
    } catch {
      setSaveState(''); // best-effort — never block the wizard on a save hiccup
    }
  }

  function goTo(idx) {
    setErrs({});
    setStepIdx(idx);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleNext() {
    const bad = validateStep();
    if (Object.keys(bad).length) {
      setErrs(bad);
      const firstId = Object.keys(bad)[0];
      document.getElementById(`wiz-field-${firstId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    saveProgress();
    goTo(stepIdx + 1);
  }

  function handleBack() {
    saveProgress();
    goTo(stepIdx - 1);
  }

  function handleSubmit() {
    // Validate every visible required field across all sections; jump to the
    // first section that still has a gap.
    for (let i = 0; i < sections.length; i++) {
      const bad = {};
      for (const f of (sections[i].fields || []).filter(f2 => isVisible(f2, answers))) {
        if (!f.required) continue;
        const v = answers[f.id];
        if (v === undefined || v === null || v === '') bad[f.id] = 'This field is required.';
      }
      if (Object.keys(bad).length) {
        setErrs(bad);
        if (i !== stepIdx) setStepIdx(i);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }
    onSubmit();
  }

  const fields = visibleFields(section);

  return (
    <div>
      {/* Progress */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        {sections.map((s, i) => (
          <div
            key={s.id}
            onClick={() => { saveProgress(); goTo(i); }}
            title={s.title}
            style={{
              flex: 1, height: 5, borderRadius: 3, cursor: 'pointer',
              background: i <= stepIdx ? '#0F1838' : '#ddd',
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555' }}>
          Step {stepIdx + 1} of {sections.length} — {section.title}
        </span>
        {onStepSave && (
          <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#1e7e5e' }}>
            {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? '✓ Saved' : ''}
          </span>
        )}
      </div>

      {/* Step body */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h2 style={{
          fontFamily: 'Arial, sans-serif', fontSize: 16, fontWeight: 700,
          color: '#0F1838', marginTop: 0, marginBottom: 16,
          paddingBottom: 10, borderBottom: '1px solid #eee',
        }}>
          {section.title}
        </h2>

        {fields.length === 0 && (
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>
            Nothing to answer in this step — continue to the next one.
          </div>
        )}

        {fields.map(field => (
          <div
            key={field.id}
            id={`wiz-field-${field.id}`}
            style={{
              marginBottom: 18,
              // Conditional follow-ups get the mockup's indent + accent border.
              ...(field.show_if ? { borderLeft: '2px solid #0F1838', paddingLeft: 14, marginLeft: 2 } : {}),
            }}
          >
            {field.show_if && (
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#0F1838', marginBottom: 4 }}>
                ↳ Because of your answer above
              </div>
            )}
            <label style={{
              fontFamily: 'Arial, sans-serif', fontSize: 14, fontWeight: 600,
              color: '#222', display: 'block', marginBottom: 4,
            }}>
              {field.label}
              {field.required && <span style={{ color: '#B85042', marginLeft: 3 }}>*</span>}
            </label>
            {field.instructions && (
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#666', marginBottom: 5 }}>
                {field.instructions}
              </div>
            )}
            <FieldInput
              field={field}
              value={answers[field.id]}
              onChange={val => {
                onChange(field.id, val);
                if (errs[field.id]) setErrs(prev => { const n = { ...prev }; delete n[field.id]; return n; });
              }}
            />
            {errs[field.id] && (
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#B85042', marginTop: 4 }}>
                {errs[field.id]}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 40 }}>
        <button
          onClick={handleBack}
          disabled={stepIdx === 0}
          style={{
            fontSize: 14, fontFamily: 'Arial, sans-serif', padding: '10px 22px',
            borderRadius: 4, border: '1px solid #ccc', background: '#fff',
            color: stepIdx === 0 ? '#bbb' : '#0F1838', fontWeight: 600,
            cursor: stepIdx === 0 ? 'default' : 'pointer',
          }}
        >
          ← Back
        </button>
        {isLast ? (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              fontSize: 14, fontFamily: 'Arial, sans-serif', padding: '10px 28px',
              borderRadius: 4, border: 'none', background: '#0F1838', color: '#fff',
              fontWeight: 600, cursor: submitting ? 'default' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Submitting…' : (submitLabel || 'Submit')}
          </button>
        ) : (
          <button
            onClick={handleNext}
            style={{
              fontSize: 14, fontFamily: 'Arial, sans-serif', padding: '10px 28px',
              borderRadius: 4, border: 'none', background: '#0F1838', color: '#fff',
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Next →
          </button>
        )}
      </div>
    </div>
  );
}
